import { createRouter, createWebHistory } from 'vue-router';

import { getAdminSession } from './auth/session';

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('./views/LoginView.vue'),
      meta: { title: '后台登录', public: true, standalone: true },
    },
    {
      path: '/account-recovery',
      name: 'account-recovery',
      component: () => import('./views/AccountRecoveryView.vue'),
      meta: { title: '重置密码', public: true, standalone: true },
    },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('./views/DashboardView.vue'),
      meta: { title: '经营总览' },
    },
    {
      path: '/content/homepage',
      name: 'homepage-editor',
      component: () => import('./views/HomepageEditorView.vue'),
      meta: { title: '小程序首页装修' },
    },
    {
      path: '/content/pages',
      name: 'micro-pages',
      component: () => import('./views/MicroPagesView.vue'),
      meta: { title: '微页面管理' },
    },
    {
      path: '/content/assets',
      name: 'content-assets',
      component: () => import('./views/ContentAssetsView.vue'),
      meta: { title: '首页素材库' },
    },
    {
      path: '/content/showcase',
      name: 'showcase-review',
      component: () => import('./views/ShowcaseReviewView.vue'),
      meta: { title: '交付展示' },
    },
    {
      path: '/vehicles',
      name: 'vehicle-admin',
      component: () => import('./views/VehicleAdminView.vue'),
      meta: { title: '车辆资产' },
    },
    {
      path: '/vehicles/taxonomy',
      name: 'taxonomy',
      component: () => import('./views/TaxonomyView.vue'),
      meta: { title: '品牌与分类' },
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('./views/CustomersView.vue'),
      meta: { title: '客户公海' },
    },
    {
      path: '/customers/scope-verification',
      name: 'customer-scope-verification',
      component: () => import('./views/CustomerScopeVerificationView.vue'),
      meta: { title: '历史客户归属核实' },
    },
    {
      path: '/customers/identity-verifications',
      name: 'identity-verifications',
      component: () => import('./views/IdentityVerificationView.vue'),
      meta: { title: '身份资料审核' },
    },
    {
      path: '/rental-requests',
      name: 'rental-requests',
      component: () => import('./views/RentalRequestsView.vue'),
      meta: { title: '租赁请求' },
    },
    {
      path: '/contracts/drafts',
      name: 'contract-drafts',
      component: () => import('./views/ContractDraftsView.vue'),
      meta: { title: '合同方案试算' },
    },
    {
      path: '/finance',
      name: 'finance',
      component: () => import('./views/FinanceView.vue'),
      meta: { title: '账单与线下收款' },
    },
    {
      path: '/fulfillment',
      name: 'fulfillment',
      component: () => import('./views/FulfillmentView.vue'),
      meta: { title: '订单履约中心' },
    },
    {
      path: '/finance/cases',
      name: 'finance-cases',
      component: () => import('./views/FinanceCasesView.vue'),
      meta: { title: '资金事项控制台' },
    },
    {
      path: '/finance/deposit-offset-requests',
      name: 'finance-deposit-offset-requests',
      component: () => import('./views/DepositOffsetRequestsView.vue'),
      meta: { title: '保证金抵扣申请' },
    },
    {
      path: '/finance/partner-payables',
      name: 'finance-partner-payables',
      component: () => import('./views/PartnerPayablesView.vue'),
      meta: { title: '合作方应付' },
    },
    {
      path: '/organization/staff',
      name: 'staff',
      component: () => import('./views/StaffView.vue'),
      meta: { title: '员工与权限' },
    },
    {
      path: '/organization/stores',
      name: 'stores',
      component: () => import('./views/StoresView.vue'),
      meta: { title: '门店管理' },
    },
    {
      path: '/organization/transfers',
      name: 'manager-transfers',
      component: () => import('./views/ManagerTransfersView.vue'),
      meta: { title: '经理交接' },
    },
    {
      path: '/organization/party-profiles',
      name: 'party-profiles',
      component: () => import('./views/PartyProfilesView.vue'),
      meta: { title: '合同甲方档案' },
    },
    {
      path: '/system/readiness',
      name: 'readiness',
      component: () => import('./views/ReadinessView.vue'),
      meta: { title: '上线准备' },
    },
    {
      path: '/system/password-reset',
      name: 'password-reset',
      component: () => import('./views/PasswordResetView.vue'),
      meta: { title: '员工密码重置' },
    },
    {
      path: '/system/release-signoffs',
      name: 'release-signoffs',
      component: () => import('./views/ReleaseSignoffsView.vue'),
      meta: { title: 'Release发布确认' },
    },
    {
      path: '/system/dicts',
      name: 'business-dicts',
      component: () => import('./views/DictView.vue'),
      meta: { title: '业务字典' },
    },
    {
      path: '/system/audit-log',
      name: 'audit-log',
      component: () => import('./views/AuditLogView.vue'),
      meta: { title: '审计日志' },
    },
    {
      path: '/system/integrations',
      name: 'integration-config',
      component: () => import('./views/IntegrationConfigView.vue'),
      meta: { title: '第三方集成' },
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

/** 路由前缀 → 所需权限（any-of：任一满足即放行；与 App.vue 菜单条件完全对齐） */
const ROUTE_PERMISSIONS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['/content/assets', ['content.asset.manage']],
  ['/content/showcase', ['showcase.read']],
  ['/content', ['content.homepage.read']],
  ['/vehicles/taxonomy', ['vehicle.manage']],
  ['/vehicles', ['vehicle.read']],
  ['/customers/scope', ['customer.scope_verification.read']],
  ['/customers/identity', ['customer.identity.read']],
  ['/customers', ['customer.pool.read']],
  ['/rental-requests', ['rental_request.read']],
  ['/contracts', ['contract.read.self', 'contract.read.store']],
  ['/finance/cases', ['finance.bill.read']],
  ['/finance/deposit', ['finance.deposit.deduct.create']],
  ['/finance/partner', ['finance.partner_payable.read']],
  ['/finance', ['finance.bill.read', 'finance.bank_transfer.read']],
  ['/fulfillment', ['order.fulfillment.read']],
  ['/organization/staff', ['organization.staff.read']],
  ['/organization', ['organization.store.read']],
];

router.beforeEach((route) => {
  if (route.meta.public === true) {
    if (route.name === 'login' && getAdminSession()) return { name: 'dashboard' };
    return true;
  }
  if (!getAdminSession()) return { name: 'login', query: { redirect: route.fullPath } };
  const required = ROUTE_PERMISSIONS.find(([prefix]) => route.path.startsWith(prefix))?.[1];
  if (required) {
    try {
      const perms: readonly string[] = JSON.parse(localStorage.getItem('admin_perms') ?? '[]');
      if (Array.isArray(perms) && perms.length > 0 && !required.some((perm) => perms.includes(perm))) {
        return { name: 'dashboard' };
      }
    } catch {
      /* 无快照放行，后端 403 兜底 */
    }
  }
  return true;
});

router.afterEach((route) => {
  const title = typeof route.meta.title === 'string' ? route.meta.title : '管理中心';
  document.title = `${title} · 聚車缘`;
});
